import React from "react";
import config from "../../../config";
import { ApiHandlerPureComponent } from "./ApiHandlerPureComponent";
import { User } from "../interfaces/User";
import { Claim, UserManagePayload } from "../../usersManagement/interfaces";
import { UserRoles } from "../../usersManagement/UserRolesEnum";
import { userRoleStringRemoveSpaceAndCaptialiseFirstLetter } from "../../usersManagement/utilities";
import { useTranslation } from "react-i18next";
import { UserRolesConstants } from "../constants/UserRolesConstants";

interface Props {
  onAuthError?: () => void;
}

const CUSTOMER_SUPER_ADMIN: string = "CustomerSuperAdmin";
const MANAGED_SURVEYS_SUPER_ADMIN: string = "ManagedSurveysSuperAdmin";

export default class AccountsApis extends React.PureComponent<Props> {
  private apiResponseHandler = new ApiHandlerPureComponent({
    onAuthError: this.props.onAuthError,
  });
  private t = useTranslation();

  private static readonly BASE_URL: string = `${config.apiGateway.AUTH_API}/api/Account`;

  getUsers = async (): Promise<User[]> => {
    const data = await this.apiResponseHandler.get(`${AccountsApis.BASE_URL}/users`);
    if (!data) {
      return [];
    }
    if (!Array.isArray(data)) {
      return [];
    }
    return data;
  };

  getClaimsByUser = async (username: string): Promise<Claim[] | null> => {
    const data = await this.apiResponseHandler.get(`${AccountsApis.BASE_URL}/claims/${username}`);
    if (!data) {
      return null;
    }
    return data;
  };

  createUser = async (user: UserManagePayload): Promise<string> => {
    const data = await this.apiResponseHandler.post(
      `${AccountsApis.BASE_URL}/createUser/user/${user.email}/email/${user.email}`,
    );
    if (!data) {
      return "";
    }
    return data;
  };

  addClaim = async (email: string, claim: Claim): Promise<string> => {
    const data = await this.apiResponseHandler.post(
      `${AccountsApis.BASE_URL}/addClaim/user/${email}/type/${claim.type}/value/${claim.value}`,
    );
    if (!data) {
      return "";
    }
    return data;
  };

  deleteClaim = async (email: string, claim: Claim): Promise<string> => {
    const data = await this.apiResponseHandler.delete(
      `${AccountsApis.BASE_URL}/claims/${email}/type/${claim.type}/value/${claim.value}`,
    );
    if (!data) {
      return "";
    }
    return data;
  };

  updateClaims = async (userPayload: UserManagePayload): Promise<string> => {
    if (!userPayload.email) {
      return "";
    }

    let filteredCurrentClaims = await this.getUserRoleFilteredClaims(userPayload.email);
    if (!filteredCurrentClaims) filteredCurrentClaims = [];

    const claimsToDelete = await this.getClaimsToDelete(filteredCurrentClaims, userPayload);

    if (claimsToDelete) {
      const modifiedClaims = claimsToDelete.map((claim) => {
        if (claim.type === UserRolesConstants.CustomerAdminUser) {
          return {
            ...claim,
            type: claim.type.replace(UserRolesConstants.CustomerAdminUser, UserRolesConstants.CustomerUTPAdmin),
          };
        }
        if (claim.type === CUSTOMER_SUPER_ADMIN) {
          return {
            ...claim,
            type: claim.type.replace(CUSTOMER_SUPER_ADMIN, MANAGED_SURVEYS_SUPER_ADMIN),
          };
        }
        return claim;
      });
      for (const claim of modifiedClaims) {
        await this.deleteClaim(userPayload.email, claim);
      }
    }

    const claimsToAdd = await this.getClaimsToAdd(filteredCurrentClaims, userPayload.claims);

    if (claimsToAdd) {
      for (const claim of claimsToAdd.filter((claim) => claim?.type !== "" && claim?.value != null)) {
        if (claim != null) {
          await this.addClaim(userPayload.email, claim);
        }
      }
    }

    return "Claims Updated";
  };

  private getUserRoleFilteredClaims = async (email: string) => {
    const currentClaims = await this.getClaimsByUser(email);

    return currentClaims?.filter((claim) => {
      if (claim.type.trim().toLowerCase() === UserRolesConstants.CustomerUTPAdmin.trim().toLowerCase()) {
        return true;
      }
      const userRoles = Object.values(UserRoles).map((role) => userRoleStringRemoveSpaceAndCaptialiseFirstLetter(role));
      return userRoles.some((userRole) => claim.type.endsWith(userRole));
    });
  };

  private getClaimsToDelete = async (
    filteredCurrentClaims: Claim[],
    userPayload: UserManagePayload,
  ): Promise<Claim[]> => {
    return filteredCurrentClaims.filter(
      (claim) =>
        !userPayload.claims.find(
          (userClaim) =>
            userClaim.type.toString() === claim.type.toString() &&
            (userClaim && userClaim && userClaim.value && userClaim.value.toString()) ===
              (claim && claim.value && claim.value.toString()),
        ),
    );
  };

  private getClaimsToAdd = async (filteredCurrentClaims: Claim[], userPayload: Claim[]) => {
    let updatedPayload = userPayload
      .map((claim) => {
        if (claim.type.endsWith(UserRolesConstants.CustomerAdminUser)) {
          return {
            ...claim,
            type: claim.type.replace(UserRolesConstants.CustomerAdminUser, UserRolesConstants.CustomerUTPAdmin),
          };
        }
        return claim;
      })
      .filter((claim) => claim != null);
    return updatedPayload.filter(
      (userClaim) =>
        !filteredCurrentClaims?.find(
          (claim) =>
            userClaim?.type.toString() === claim.type.toString() &&
            userClaim &&
            userClaim &&
            userClaim.value &&
            userClaim.value.toString() === (claim && claim.value && claim.value.toString()),
        ),
    );
  };
}
